import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";

import { Skeleton, Table, Tooltip } from "antd";
import { FaChevronDown, FaChevronUp, FaCopy, FaInfo, FaInfoCircle, FaRegThumbsDown, FaRegThumbsUp } from "react-icons/fa";

import "./styles.scss";
import ESGUserIcon from "../../../../assets/images/ESG-Chat-User-Icon.svg";
import ESGLogoIcon from "../../../../assets/images/ESG-Chat-logo-icon.svg";
import ESGSourceIcon from "../../../../assets/images/ESG-Chat-Source-Icon.svg";
import { FileIcons } from "../../../../assets/images/svg-icons";
import { ESGChatSignedUrlAction, ESGChatSignedUrlReset } from "../../../../store/slices/ai-chat/esgChat";
import { S3URLRequestReset, S3URLRequestAction } from "../../../../store/slices/s3urlRequest";
import { TextConcat } from "../../../../helpers/text-concat";
import { copyToClipboard } from "../../../../helpers/clipboard-copy";


const feedbackData = [
    {
        id: 1,
        label: "Answer Found",
        value: "answer found",
        icon: <FaRegThumbsUp />,
        className: "answer-found",
    },
    {
        id: 2,
        label: "Answer Not Found",
        value: "answer not found",
        icon: <FaRegThumbsDown />,
        className: "answer-not-found",
    },
];

export const tableHeaders = (promptMode) => {
    const isModifyPrompt = promptMode === "Modify-Prompt";

    const commonColumns = [
        {
            title: <span style={{ fontWeight: 'bold' }}>Response</span>,
            dataIndex: 'response',
            key: 'response',
            width: 500,
            render: (text) => <TextConcat text={text} maxLength={100} />,
        },
        {
            title: <span style={{ fontWeight: 'bold' }}>Response Unit</span>,
            dataIndex: 'response_unit',
            key: 'response_unit',
            width: 500,
        },
        {
            title: <span style={{ fontWeight: 'bold' }}>Place Value</span>,
            dataIndex: 'place_value',
            key: 'place_value',
            width: 500,
        },
        {
            title: <span style={{ fontWeight: 'bold' }}>Text Snippet</span>,
            dataIndex: 'text_snippet',
            key: 'text_snippet',
            width: 500,
            render: (text) => <TextConcat text={text} maxLength={100} />,
        },
        {
            title: <span style={{ fontWeight: 'bold' }}>Page Number</span>,
            dataIndex: 'pageNumber',
            key: 'pageNumber',
            width: 500,
            render: (text) => <TextConcat text={text} maxLength={100} />,
        },
        {
            title: <span style={{ fontWeight: 'bold' }}>Justification</span>,
            dataIndex: 'justification',
            key: 'justification',
            width: 500,
            render: (text) => <TextConcat text={text} maxLength={100} />,
        },
        {
            title: <span style={{ fontWeight: 'bold' }}>Confidence Score</span>,
            dataIndex: 'confidenceScore',
            key: 'confidenceScore',
            width: 500,
            render: (text) => <TextConcat text={text} maxLength={100} />,
        },
        {
            title: <span style={{ fontWeight: 'bold' }}>Borderline Case</span>,
            dataIndex: 'borderlineCase',
            key: 'borderlineCase',
            width: 500,
            render: (text) => <TextConcat text={text} maxLength={100} />,
        },
        {
            title: <span style={{ fontWeight: 'bold' }}>Search Query</span>,
            dataIndex: 'searchQuery',
            key: 'searchQuery',
            width: 500,
            render: (text) => <TextConcat text={text} maxLength={100} />,
        },

    ].filter(Boolean); // Removes the false entry when isModifyPrompt is true

    const fileOrSourceColumns = isModifyPrompt
        ? [
            {
                title: <span className="kv-key">File Name</span>,
                dataIndex: 'fileName',
                key: 'fileName',
                width: 550,
            },
            {
                title: <span className="kv-key">File Id</span>,
                dataIndex: 'fileId',
                key: 'fileId',
                width: 550,
            },
            {
                title: <span className="kv-key">Notes</span>,
                dataIndex: 'notes',
                key: 'notes',
                width: 550,
            },
        ]
        : [];

    // const sourceUrlColumn = {
    //     title: <span style={{ fontWeight: 'bold' }}>Source URL</span>,
    //     dataIndex: 'sourceUrl',
    //     key: 'sourceUrl',
    //     width: 550,
    //     render: (text) =>
    //         text?.trim() && text !== 'NA' ? (
    //             <a
    //                 href={text}
    //                 target="_blank"
    //                 rel="noopener noreferrer"
    //                 style={{ color: '#1890ff', textDecoration: 'underline' }}
    //             >
    //                 Open Link
    //             </a>
    //         ) : null,
    // };

    const sourceDetailsColumn = {
        title: <span className="kv-key">Source Details</span>,
        key: 'sourceDetails',
        width: 650,
        render: (_, record) => {
            const { sourceType, sourceTitle, sourceUrl } = record;

            return (
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <span>
                        {(sourceType || "NA") +
                            " / " +
                            (sourceTitle || "NA")}
                    </span>

                    {sourceUrl?.trim() && sourceUrl !== "NA" ? (
                        <a
                            href={sourceUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: '#1890ff', textDecoration: 'underline' }}
                        >
                            Open Link
                        </a>
                    ) : (
                        <span style={{ color: '#999' }}>No URL</span>
                    )}
                </div>
            );
        },
    };

    return [...commonColumns, ...fileOrSourceColumns, sourceDetailsColumn];
};


export const ChatResponseRef = ({
    data,
    showUploadSection,
    handlePDFPreview,
    isControversyChat,
    setGoToPage,
    promptEvaluationFlag,
    chunkEvaluation,
    score,
    index
}) => {

    const [showDetails, setShowDetails] = useState(chunkEvaluation ? true : false);

    const dispatch = useDispatch();
    const truncatedText = `${data?.text.slice(0, 350)}... `;

    const getSignedURL = async ({ sourceFile, searchType }) => {
        dispatch(ESGChatSignedUrlReset());
        dispatch(S3URLRequestReset());

        if (["Custom-Comparison", "Custom"]?.includes(searchType)) {
            const payload = {
                url: data?.pdfLink,
            };

            dispatch(ESGChatSignedUrlAction(payload));
            handlePDFPreview({ url: data?.pdfLink });
        } else {
            dispatch(S3URLRequestAction(sourceFile));
            handlePDFPreview({ url: sourceFile });
        }
        return null;
    };

    const handlePDFOpen = () => {
        const pdfUrl = data?.pageNo
            ? `${data?.pdfLink}#page=${data?.pageNo}`
            : data?.pdfLink;

        window.open(pdfUrl, "_blank");
    }

    const handleJumpToPage = (e) => {
        e.stopPropagation();
        e.preventDefault();
        setGoToPage(data?.pageNo || 0);
        getSignedURL({
            sourceFile: data?.pdfLink,
            searchType: data?.searchType,
        });
    }

    useEffect(() => {
        dispatch(ESGChatSignedUrlReset());
        dispatch(S3URLRequestReset());
    }, []);


    return (
        <div className="chat-references-description-card bedrock-references">
            {chunkEvaluation && <div className="reference-score-info">
                <Tooltip title={
                    <div>
                        <div>Score: {data?.score}</div>
                        <div>FTS Score: {data?.fts_score}</div>
                        <div>VS Score: {data?.vs_score}</div>
                    </div>
                }><FaInfoCircle /></Tooltip>
            </div>}
            <div className="reference-headers">
                {index}.{data?.pdfLink && (
                    <div
                        className="download-source-section"
                        style={{ color: "#50bfa5" }}
                        onClick={() => chunkEvaluation ? handlePDFOpen() :
                            getSignedURL({
                                sourceFile: data?.pdfLink,
                                searchType: data?.searchType,
                            })
                        }
                    >
                        <FileIcons type="pdf" />
                    </div>

                )}
                {showUploadSection ? (
                    <div className="source-title">
                        {data?.companyName || data?.sourceName}
                    </div>
                ) : (
                    <div className="source-title">
                        {data?.sourceName}

                        {isControversyChat ? (
                            <small className="source-reference-details">
                                {data?.sourceURI && data?.sourceURILink && (
                                    <a
                                        className="source-reference-details-link"
                                        target="_blank"
                                        href={data?.sourceURILink || ""}
                                    >
                                        {data?.sourceURI ? ` | ${data?.sourceURI}` : ""}
                                    </a>
                                )}
                                {data?.date ? ` | ${data?.date}` : ""}
                            </small>
                        ) : (
                            <small className="source-reference-details">
                                {data?.publicationDate ? ` | ${data?.publicationDate}` : ""}
                                {data?.companyName ? ` | ${data?.companyName}` : ""}
                                {data?.pageNo ? (
                                    <>
                                        | Page No: <a className="page-reference-details-link" href="#" onClick={(e) => handleJumpToPage(e)}> {data?.pageNo}</a>

                                    </>
                                ) : " | Page No: NA"}
                                {` | ${promptEvaluationFlag ? data?.sourceSubTypeName || data?.sourceTypeName || "" : data?.sourceTypeName || ""}`}
                            </small>
                        )}
                    </div>
                )}
            </div>
            <div className="text-section">
                <div className="text-content">
                    {showDetails ? data?.text : truncatedText}
                    <span
                        className="show-option"
                        onClick={() => setShowDetails(!showDetails)}
                    >
                        {showDetails ? "Show less" : "Show more"}
                    </span>
                </div>
            </div>
        </div>
    );
};

export const ChatLoadingCard = ({ question, promptEvaluationFlag, chunkEvaluation }) => {

    return (
        <div className="chat-card loading-card">
            <div className="question">
                <div className="left-section">
                    <img
                        className="icons esg-user"
                        src={ESGUserIcon}
                        alt="ESG user icon"
                    />
                </div>
                <div className="right-section">{question}</div>
            </div>
            {!chunkEvaluation && <div className="answers">
                <div className="left-section">
                    <img
                        className="icons esg-logo"
                        src={ESGLogoIcon}
                        alt="ESG user icon"
                    />
                </div>
                <div className="right-section">
                    <Skeleton
                        className="loader-skeleton"
                        active
                        paragraph={{ rows: 0 }}
                    />
                </div>
            </div>}
            <>
                <div className="section-title">
                    <div className="left-section">
                        <img
                            className="icons esg-source"
                            src={ESGSourceIcon}
                            alt="ESG chat souce icon"
                        />
                    </div>
                    <div className="right-section">References</div>
                </div>
                <div className="sources">
                    <Skeleton
                        className="loader-skeleton"
                        active
                        paragraph={{ rows: 1 }}
                    />
                </div>
            </>
        </div>
    )
}

const ChatCard = ({
    data,
    chatIndex,
    isControversyChat,
    setQuestion,
    showFeedback,
    feedbackLoading,
    feedbackSuccess,
    handleFeedback,
    handlePDFPreview,
    setGoToPage,
    showUploadSection,
    promptEvaluationFlag,
    setSelectedQuestion,
    setPlaybookId,
    promptMode,
    chunkEvaluation,
    setSliderValue
}) => {

    const [expandSource, setExpandSource] = useState(false);
    const [copyQuestion, setCopyQuestion] = useState(false);
    const [copyAnswer, setCopyAnswer] = useState(false);
    const [currentFeedback, setCurrentFeedback] = useState(null)
    const [expandedEntity, setExpandedEntity] = useState(null);

    const handleCollapse = () => {
        setExpandSource(!expandSource);
    };

    const handleToggleEntity = (entityName) => {
        setExpandedEntity(prev => prev === entityName ? null : entityName);
    }

    const handleCopyQuestion = (question) => {
        setQuestion(question);
        setCopyQuestion(true);
        if (data?.response || chunkEvaluation) {
            setPlaybookId(data?.dpDetails?.playbookId)
            setSelectedQuestion((prev) => ({
                ...prev,
                selectedRow: data?.dpDetails
            }));
            if (chunkEvaluation) {
                setSliderValue({
                    keywordWeight: data?.dpDetails?.fts,
                    vectorWeight: data?.dpDetails?.vts
                })
            }
        }
        navigator.clipboard.writeText(question);

        setTimeout(() => {
            setCopyQuestion(false);
        }, 2000);
    };

    const handleCopyAnswer = () => {
        const answer = document.getElementById(`chat-answer-text-${chatIndex}`);
        if (answer) {
            copyToClipboard(answer);
            setCopyAnswer(true);

            setTimeout(() => {
                setCopyAnswer(false);
            }, 2000);
        }
    };

    const handleCurrentFeedback = ({ feed, data }) => {
        setCurrentFeedback(feed)
        handleFeedback(feed.value, data)
    }

    const headerMap = {};
    tableHeaders(promptMode)?.forEach(col => {
        headerMap[col.dataIndex] = col.title;
    });


    const formattedData = (data) => {
        if (data?.answer) {
            return <div>{data?.answer}</div>
        }
        let formattedResponse;
        if (Array.isArray(data?.response) && data?.response?.length === 1) {
            formattedResponse = data?.response?.toString();
        } else {
            formattedResponse = data?.response || "";
        }

        const dataSource = {
            response: formattedResponse,
            response_unit: data?.response_unit || "-",
            place_value: data?.place_value || "-",
            text_snippet: data?.text_snippet,
            pageNumber: data?.pageNumber || "-",
            sourceDetails: {
                sourceType: data?.sourceType || "-",
                sourceTitle: data?.sourceTitle || "-",
                sourceUrl: data?.sourceUrl || "-",
            },
            fileName: data?.fileName,
            fileId: data?.fileId,
            notes: data?.notes,
            justification: data?.justification,
            confidenceScore: data?.confidenceScore,
            borderlineCase: data?.borderlineCase ? "Yes" : "No",
            searchQuery: data?.searchQuery || "",
        }

        return (
            // <div className="table-copyanswer-wrapper">
            //     <Table
            //         className="formatted-response-table"
            //         columns={tableHeaders(promptMode)}
            //         pagination={false}
            //         dataSource={[dataSource]}
            //     />
            // </div>
            <div className="key-value-wrapper">
                {Object.entries(dataSource)?.map(([key, value]) => {
                    if (!value) return null;

                    const displayTitle = headerMap[key] || key;

                    if (key === "sourceDetails" && typeof value === "object") {
                        const { sourceType, sourceTitle, sourceUrl } = value;

                        const allEmpty =
                            [sourceType, sourceTitle, sourceUrl]?.every(
                                (v) => !v || v?.trim() === "-" || v?.trim() === "NA"
                            );

                        return (
                            <div className="kv-row" key={key}>
                                <div className="kv-key">Source Details</div>
                                <div className="kv-value">
                                    {allEmpty ? (
                                        <span>-</span>
                                    ) : (
                                        <div>
                                            {sourceType && sourceType !== "-" ? sourceType : ""}
                                            {sourceTitle && sourceTitle !== "-" ? ` / ${sourceTitle}` : ""}
                                            {sourceUrl?.trim() && sourceUrl !== "NA" && sourceUrl !== "-" ? (
                                                <>
                                                    {" / "}
                                                    <a
                                                        href={sourceUrl}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        style={{ color: "#1890ff", textDecoration: "underline" }}
                                                    >
                                                        Open Link
                                                    </a>
                                                </>
                                            ) : (
                                                ""
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    }

                    return (
                        <div className="kv-row" key={key}>
                            <div className="kv-key">{displayTitle}</div>
                            <div className="kv-value">{value}</div>
                        </div>
                    );
                })}
            </div>
        )
    }

    useEffect(() => {
        const container = document.getElementById(`chat-answer-text-${chatIndex}`);
        if (container) {
            const links = container.querySelectorAll("a");
            links.forEach((link) => {
                link.setAttribute("target", "_blank");
                link.setAttribute("rel", "noopener noreferrer")
            })
        }
    }, [data?.answer, chatIndex])

    return (
        <div className="chat-card">
            <div className="question">
                <div className="left-section">
                    <img className="icons esg-user" src={ESGUserIcon} alt="ESG icon" />
                </div>
                <div className="right-section">
                    <div className="question-container">
                        {data?.question}
                        <span
                            className="regenerate-question"
                            onClick={() => handleCopyQuestion(data?.question)}
                        >
                            <Tooltip title={!copyQuestion ? "Copy" : "Copied"}>
                                <FaCopy />
                            </Tooltip>
                        </span>
                    </div>

                    {/* Feedback Options */}
                    {showFeedback && !feedbackSuccess && (
                        <div className="feedback-container">
                            {!feedbackLoading &&
                                feedbackData?.map((feed) => {
                                    return (
                                        <Tooltip key={feed.id} title={feed.label}>
                                            <div
                                                className={`feed-card ${feed.className}`}
                                                key={feed.id}
                                                onClick={() => handleCurrentFeedback({ feed: feed, data: data })}
                                            >
                                                {feed.icon}
                                            </div>
                                        </Tooltip>
                                    );
                                })}
                        </div>
                    )}
                    {/* Selected Feedback */}
                    {(currentFeedback && feedbackSuccess) && (
                        <div className="feedback-container">
                            <Tooltip key={currentFeedback.id} title={currentFeedback.label}>
                                <div className={`feed-card ${currentFeedback.className}`}>
                                    {currentFeedback.icon}
                                </div>
                            </Tooltip>

                        </div>
                    )}
                </div>
            </div>
            {(!chunkEvaluation || data?.answer) && <div className="answers">
                <div className="left-section">
                    <img
                        className="icons esg-logo"
                        src={ESGLogoIcon}
                        alt="ESG user icon"
                    />
                </div>
                <div className="right-section entity">
                    {
                        promptEvaluationFlag ?
                            // (
                            //     data?.response?.length > 0 ?
                            //         data?.response?.map((item, index) => {
                            //             const isExpanded = expandedEntity === item.entityName;
                            //             const isArrayResponse = Array.isArray(item?.response);
                            //             const hasMultipleResponses = isArrayResponse && item?.response?.length > 1;

                            //             let formattedResponse;
                            //             if (hasMultipleResponses) {
                            //                 formattedResponse = (
                            //                     <ul>
                            //                         {item?.response?.map((res, idx) => (
                            //                             <li key={idx}>
                            //                                 <span>{res}</span>
                            //                             </li>
                            //                         ))}
                            //                     </ul>
                            //                 );
                            //             } else if (isArrayResponse && item?.response?.length === 1) {
                            //                 formattedResponse = item?.response?.toString();
                            //             } else {
                            //                 formattedResponse = item?.response || "-";
                            //             }
                            //             const formattedData = () => ({
                            //                 response: formattedResponse,
                            //                 response_unit: item?.response_unit,
                            //                 text_snippet: item?.text_snippet,
                            //                 pageNumber: item?.pageNumber,
                            //                 sourceTitle: item?.sourceTitle,
                            //                 sourceUrl: item?.sourceUrl
                            //             })
                            //             return (
                            //                 <div key={index}
                            //                     className="expand-collapse-text"
                            //                 >
                            //                     <div className="expand-collapse-button">
                            //                         <div className="entity-accordian-header" onClick={() => handleToggleEntity(item.entityName)}>

                            //                             {item.entityName}

                            //                             {isExpanded ? <FaChevronUp /> : <FaChevronDown />}
                            //                         </div>
                            //                         {isExpanded && (
                            //                             <div className="table-copyanswer-wrapper">
                            //                                 <Table
                            //                                     className="formatted-response-table"
                            //                                     id={`chat-answer-text-${chatIndex}`}
                            //                                     dataSource={[formattedData()]}
                            //                                     columns={tableHeaders}
                            //                                     pagination={false}
                            //                                 />
                            //                                 <div className="answer-action-items">
                            //                                     <Tooltip title={`${copyAnswer ? "Copied" : "Copy"}`}>
                            //                                         <div className="action-button" onClick={() => handleCopyAnswer(formattedData())}>
                            //                                             <FaCopy />
                            //                                         </div>
                            //                                     </Tooltip>
                            //                                 </div>
                            //                             </div>
                            //                         )
                            //                         }
                            //                     </div>
                            //                 </div>
                            //             )
                            //         }) 
                            //         : (
                            //             <div
                            //                 className="answer-text"
                            //                 id={`chat-answer-text-${chatIndex}`}
                            //                 dangerouslySetInnerHTML={{
                            //                     __html: data?.answer || "Answer not found in the document!",
                            //                 }}
                            //             />
                            //         )

                            // ) 
                            formattedData(data)
                            : (
                                <div
                                    className="answer-text"
                                    id={`chat-answer-text-${chatIndex}`}
                                    dangerouslySetInnerHTML={{
                                        __html: data?.answer || "Answer not found in the document!",
                                    }}
                                />
                            )
                    }
                    {!promptEvaluationFlag && <div className="answer-action-items">
                        <Tooltip title={`${copyAnswer ? "Copied" : "Copy"}`}>
                            <div className="action-button" onClick={() => handleCopyAnswer()}>
                                <FaCopy />
                            </div>
                        </Tooltip>
                    </div>}
                </div>
            </div>}
            {data?.sources?.length > 0 && (
                <div className="sources-section-header">
                    <div className="section-title">
                        <div className="left-section">
                            <img
                                className="icons esg-source"
                                src={ESGSourceIcon}
                                alt="ESG chat souce icon"
                            />
                        </div>
                        <div className="right-section-container">
                            <div className="right-section">References</div>
                            <span className="references-length">{`(${data?.sources?.length})`}</span>
                            <div
                                className="expand-collapse-text"
                                onClick={() => handleCollapse()}
                            >
                                {!expandSource ? (
                                    <div className="expand-collapse-button">
                                        Show <FaChevronDown />
                                    </div>
                                ) : (
                                    <div className="expand-collapse-button">
                                        Hide <FaChevronUp />
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
            {data?.sources && (
                <div
                    className={`sources ${expandSource ? "expanded-source" : "collapsed-source"
                        }`}
                >
                    <div className="right-section">
                        {data?.sources?.map((desc, i) => {
                            return (
                                <ChatResponseRef
                                    data={desc}
                                    key={i}
                                    index={i+1}
                                    handlePDFPreview={handlePDFPreview}
                                    showUploadSection={showUploadSection}
                                    isControversyChat={isControversyChat}
                                    setGoToPage={setGoToPage}
                                    promptEvaluationFlag={promptEvaluationFlag}
                                    chunkEvaluation={chunkEvaluation}
                                    score={data?.score}
                                />
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
};

export default ChatCard